import React, { useState, useEffect, useCallback } from 'react';
import { Card, Badge } from '../../../components/ui';
import { Wallet, RefreshCw, DollarSign } from 'lucide-react';
import { fetchAPI } from '../../../services/apiClient';

// Tipos
interface MiSaldo {
  local_id: string;
  local_nombre: string;
  saldo_actual: string;
}

interface Movimiento {
  id: string;
  local_id: string;
  local_nombre: string;
  tipo: string;
  monto: string;
  referencia: string;
  fecha: string;
  saldo_resultante: string;
  detalle: {
    vendido: string;
    premios: string;
    comision: string;
  } | null;
  created_by: string;
  created_at: string;
}

const TIPO_LABELS: Record<string, string> = {
  LIQUIDACION_DIA: 'Liquidación',
  CIERRE_CONSOLIDADO: 'Cierre',
  ABONO: 'Abono',
  PAGO: 'Pago',
};

const TIPO_BADGE: Record<string, 'primary' | 'success' | 'warning' | 'danger' | 'neutral'> = {
  LIQUIDACION_DIA: 'primary',
  CIERRE_CONSOLIDADO: 'neutral',
  ABONO: 'success',
  PAGO: 'warning',
};

const formatMonto = (val: string) => {
  const n = Number(val);
  if (isNaN(n)) return val;
  return n.toLocaleString('es-CR', { minimumFractionDigits: 0 });
};

export const LocalSaldoPage: React.FC = () => {
  const [saldo, setSaldo] = useState<MiSaldo | null>(null);
  const [movimientos, setMovimientos] = useState<Movimiento[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const cargarDatos = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [saldoData, movsData] = await Promise.all([
        fetchAPI<MiSaldo>('/saldos/mi-saldo'),
        fetchAPI<Movimiento[]>('/saldos/movimientos?limit=50'),
      ]);
      setSaldo(saldoData);
      setMovimientos(movsData);
    } catch (e: any) {
      setError(e.message || 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  const saldoNum = saldo ? Number(saldo.saldo_actual) : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mi Saldo</h1>
          <p className="text-gray-500">{saldo?.local_nombre || 'Estado financiero con la banca'}</p>
        </div>
        <button
          onClick={cargarDatos}
          className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
          title="Refrescar"
        >
          <RefreshCw size={20} />
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">{error}</div>
      )}

      {/* Saldo actual */}
      {!loading && saldo && (
        <Card>
          <div className="flex items-center justify-center py-6">
            <div className="text-center">
              <div className={`inline-flex items-center gap-3 px-8 py-4 rounded-2xl ${
                saldoNum > 0 ? 'bg-red-50 border-2 border-red-200' :
                saldoNum < 0 ? 'bg-green-50 border-2 border-green-200' :
                'bg-gray-50 border-2 border-gray-200'
              }`}>
                <Wallet size={32} className={
                  saldoNum > 0 ? 'text-red-500' :
                  saldoNum < 0 ? 'text-green-500' :
                  'text-gray-500'
                } />
                <div>
                  <p className={`text-4xl font-bold ${
                    saldoNum > 0 ? 'text-red-600' :
                    saldoNum < 0 ? 'text-green-600' :
                    'text-gray-600'
                  }`}>
                    {formatMonto(saldo.saldo_actual)}
                  </p>
                  <p className={`text-sm mt-1 ${
                    saldoNum > 0 ? 'text-red-500' :
                    saldoNum < 0 ? 'text-green-500' :
                    'text-gray-500'
                  }`}>
                    {saldoNum > 0 ? 'Debes a la banca' :
                     saldoNum < 0 ? 'La banca te debe' :
                     'Saldo en cero'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Loading */}
      {loading && (
        <div className="text-center py-8 text-gray-500">Cargando...</div>
      )}

      {/* Historial de movimientos */}
      {!loading && (
        <Card padding="none">
          <div className="px-4 py-3 border-b bg-gray-50">
            <h3 className="font-medium text-gray-700">Historial de Movimientos</h3>
          </div>
          {movimientos.length === 0 ? (
            <div className="p-8 text-center text-gray-500">Sin movimientos registrados</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Fecha</th>
                    <th className="text-center px-4 py-3 text-sm font-medium text-gray-700">Tipo</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-gray-700">Monto</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-gray-700">Saldo</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Referencia</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {movimientos.map(m => (
                    <tr key={m.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">{m.fecha}</td>
                      <td className="px-4 py-3 text-center">
                        <Badge variant={TIPO_BADGE[m.tipo] || 'neutral'}>
                          {TIPO_LABELS[m.tipo] || m.tipo}
                        </Badge>
                      </td>
                      <td className={`px-4 py-3 text-right font-mono font-medium ${Number(m.monto) >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {Number(m.monto) >= 0 ? '+' : ''}{formatMonto(m.monto)}
                      </td>
                      <td className={`px-4 py-3 text-right font-bold ${Number(m.saldo_resultante) > 0 ? 'text-red-600' : Number(m.saldo_resultante) < 0 ? 'text-green-600' : 'text-gray-600'}`}>
                        {formatMonto(m.saldo_resultante)}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">{m.referencia}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

export default LocalSaldoPage;
